import React from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import DesktopWindowsRoundedIcon from '@mui/icons-material/DesktopWindowsRounded';
import TabletMacRoundedIcon from '@mui/icons-material/TabletMacRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import PublishRoundedIcon from '@mui/icons-material/PublishRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import { BUILDER_DEVICE_MODES } from './builderArchitecture';

const DEVICE_OPTIONS = [
  { value: BUILDER_DEVICE_MODES.DESKTOP, label: 'Desktop', icon: DesktopWindowsRoundedIcon },
  { value: BUILDER_DEVICE_MODES.TABLET, label: 'Tablet', icon: TabletMacRoundedIcon },
  { value: BUILDER_DEVICE_MODES.MOBILE, label: 'Mobile', icon: PhoneIphoneRoundedIcon },
];

export default function BuilderToolbar({
  templateName = 'Website design',
  pageName = 'Home',
  syncStatus = 'unknown',
  deviceMode = BUILDER_DEVICE_MODES.DESKTOP,
  onDeviceModeChange,
  onPreview,
  onCloseFocusMode,
  focusMode = false,
  onSavePrivateTemplate,
  onApplyTemplate,
  onOpenMoreActions,
  applying = false,
  saving = false,
  canApply = true,
  hasUnsavedChanges = false,
}) {
  return (
    <Box className="ps-builder-toolbar ps-builder-toolbar--2026">
      <Box className="ps-builder-toolbar__identity">
        <Typography className="ps-builder-toolbar__template" noWrap>
          {templateName}
        </Typography>
        <Typography className="ps-builder-toolbar__slash" aria-hidden="true">
          /
        </Typography>
        <Typography className="ps-builder-toolbar__page" noWrap>
          {pageName}
        </Typography>
        <Chip
          size="small"
          className={`ps-builder-status ps-builder-status--${String(syncStatus || 'unknown').toLowerCase()}`}
          label={syncStatus === 'unknown' ? 'Not published yet' : syncStatus}
        />
        <Chip
          size="small"
          variant={hasUnsavedChanges ? 'filled' : 'outlined'}
          label={hasUnsavedChanges ? 'Unsaved changes' : 'Up to date'}
          className={`ps-builder-save-state${hasUnsavedChanges ? ' is-dirty' : ''}`}
        />
      </Box>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={deviceMode}
        onChange={(_event, value) => value && onDeviceModeChange?.(value)}
        className="ps-builder-device-toggle"
        aria-label="Preview device"
      >
        {DEVICE_OPTIONS.map(({ value, label, icon: DeviceIcon }) => (
          <ToggleButton key={value} value={value} aria-label={label}>
            <Tooltip title={label}>
              <Box component="span" className="ps-builder-device-toggle__item">
                <DeviceIcon fontSize="small" />
              </Box>
            </Tooltip>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Box className="ps-builder-toolbar__actions">
        {focusMode ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<CloseRoundedIcon />}
            onClick={onCloseFocusMode}
          >
            Close
          </Button>
        ) : null}
        <Button
          size="small"
          variant="outlined"
          startIcon={<VisibilityRoundedIcon />}
          onClick={onPreview}
        >
          Preview
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<SaveRoundedIcon />}
          onClick={onSavePrivateTemplate}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Draft'}
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<PublishRoundedIcon />}
          onClick={onApplyTemplate}
          disabled={applying || !canApply}
          className="ps-builder-publish-button"
        >
          {applying ? 'Publishing...' : 'Publish Changes'}
        </Button>
        <Tooltip title="More actions">
          <IconButton size="small" onClick={onOpenMoreActions} aria-label="More page actions">
            <MoreHorizRoundedIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
